import isActionAllowed, {
  getDestinationFlow
} from '../../../helpers/isActionAllowed/isActionAllowed';
import {
  QUESTION_DESIGNER_ACTION,
  QUESTION_TYPES
} from '../../../helpers/constants';

const clone = value => JSON.parse(JSON.stringify(value));

/**
 * Grouped / non-grouped / cross-group scope check for an ANSWER jump
 * (a choice's or branch's `nextFlow`, i.e. answerLogicJump === true).
 *
 * Ports the scope rules that `isActionAllowed` applies in the SET_QUESTION_JUMP
 * case, so answer jumps obey the same boundaries as question-level jumps:
 *   - NOT_ALLOWED_TO_JUMP_FROM_GROUPED_QUESTION_TO_NONGROUPED
 *   - NOT_ALLOWED_TO_JUMP_NONGROUPED_TO_GROUPED   (with the follow-up exception)
 *   - NOT_ALLOWED_TO_JUMP_FROM_GROUPED_QUESTION_TO_GROUP   (the cross-group family)
 *
 * `sourceQuestion` is the enriched question that owns the answer; `destination`
 * is the resolved getDestinationFlow result. Returns true when the jump crosses
 * a group boundary it may not cross.
 */
const groupScopeViolation = (sourceQuestion, destination) => {
  const sourceGroup = sourceQuestion.group;
  const destFlow = destination.flow;
  const destIsGroupedQuestion =
    !destination.isGroup && !!destFlow && !!destFlow.group;

  // grouped question -> non-grouped question
  if (sourceGroup && !destination.isGroup && destFlow && !destFlow.group) {
    return true;
  }

  // non-grouped question -> grouped question.
  // Exception: a multi-answer MC answer jumping into a question that lives in
  // that very question's own follow-up group (this is the follow-up wiring).
  if (!sourceGroup && destIsGroupedQuestion) {
    const ownFollowUpTarget =
      sourceQuestion.type === QUESTION_TYPES.MULTIPLE_CHOICE &&
      sourceQuestion.selectAtMost > 1 &&
      destFlow.group.followUp === sourceQuestion.id;
    if (!ownFollowUpTarget) {
      return true;
    }
  }

  // non-grouped question -> level-2 (child) group node
  if (
    !sourceGroup &&
    destination.isGroup &&
    destFlow &&
    destFlow.nestingLevel === 2
  ) {
    return true;
  }

  // level-1 grouped question -> level-1 group node
  if (
    sourceGroup &&
    destination.isGroup &&
    sourceGroup.nestingLevel === 1 &&
    destFlow &&
    destFlow.nestingLevel === 1
  ) {
    return true;
  }

  // level-2 grouped question -> question whose group is level-1
  if (
    sourceGroup &&
    sourceGroup.nestingLevel === 2 &&
    destIsGroupedQuestion &&
    destFlow.group.nestingLevel === 1
  ) {
    return true;
  }

  // grouped question -> question in a different group
  if (
    sourceGroup &&
    destIsGroupedQuestion &&
    destFlow.group.id &&
    sourceGroup.id !== destFlow.group.id
  ) {
    return true;
  }

  // level-2 grouped question -> a different parent-group hierarchy
  if (
    sourceGroup &&
    sourceGroup.nestingLevel === 2 &&
    sourceGroup.parentGroup &&
    destFlow
  ) {
    const intoDifferentChildParent =
      destFlow.group &&
      destFlow.group.nestingLevel === 2 &&
      destFlow.group.parentGroup &&
      destFlow.group.parentGroup.id !== sourceGroup.parentGroup.id;
    const intoDifferentParent =
      destFlow.parentGroup &&
      destFlow.parentGroup.id !== sourceGroup.parentGroup.id;
    if (intoDifferentChildParent || intoDifferentParent) {
      return true;
    }
  }

  return false;
};

export default function clearNotAllowed(surveyLists, structure) {
  if (!structure || !structure.content) {
    return structure;
  }

  // Never mutate the caller's object.
  const cleaned = clone(structure);
  const questions = cleaned.questions || {};
  const groups = (cleaned.content && cleaned.content.groups) || [];

  // Without an enriched list there is nothing to validate against.
  if (!Array.isArray(surveyLists) || surveyLists.length === 0) {
    return { content: cleaned.content, questions: cleaned.questions };
  }

  // ---------------------------------------------------------------- helpers

  // True only when the target genuinely does not resolve. On any error we
  // return false (treat as resolvable) so we never delete a jump by accident.
  const isDangling = destinationId => {
    try {
      return getDestinationFlow(destinationId, surveyLists, cleaned) === null;
    } catch (error) {
      return false;
    }
  };

  // Enriched source entry for a question / distributor.
  const findQuestionFlow = id => surveyLists.find(flow => flow.id === id);

  // Enriched source object for a group, taken straight from surveyLists so it
  // carries the exact nestingLevel / parentGroup / randomized data the
  // validator expects (a group surfaces as the `.group` of its members).
  const findGroupFlow = groupId => {
    const asGroup = surveyLists.find(
      flow => flow.group && flow.group.id === groupId
    );
    if (asGroup) return asGroup.group;

    const asParent = surveyLists.find(
      flow =>
        flow.group &&
        flow.group.parentGroup &&
        flow.group.parentGroup.id === groupId
    );
    if (asParent) return asParent.group.parentGroup;

    return null;
  };

  // Verdict for an answer jump (a choice's or branch's `nextFlow`). The
  // "source" of an answer jump is the QUESTION that owns the answer:
  // choices/branches have no position of their own, they sit where the
  // question sits. So source identity/position come from the question, and
  // only the `target` varies per answer.
  const isAnswerJumpAllowed = (sourceQuestion, target) => {
    if (!sourceQuestion) return true; // cannot judge -> keep

    let destination;
    try {
      destination = getDestinationFlow(target, surveyLists, cleaned);
    } catch (error) {
      return true; // resolution error -> keep, never delete by accident
    }
    if (destination === null) return false; // dangling: target no longer exists
    if (target === sourceQuestion.id) return false; // routes back to its own question

    // backward: the answer sits at the question's position, so compare the
    // target's position (first inner question when it is a group) against it.
    const fromIndex = sourceQuestion.globalIndex;
    const toIndex = destination.isGroup
      ? destination.firstQuestionFlow &&
        destination.firstQuestionFlow.globalIndex
      : destination.flow && destination.flow.globalIndex;
    if (fromIndex && toIndex && fromIndex > toIndex) return false; // backward

    // grouped / non-grouped / cross-group scope — the same boundaries
    // isActionAllowed enforces for question-level jumps, ported for answers
    // (the follow-up exception keeps legitimate MAMC follow-up jumps intact).
    if (groupScopeViolation(sourceQuestion, destination)) {
      return false;
    }

    return true;
  };

  // Delegate question / group jump verdicts to the real validator.
  const isJumpAllowed = (action, sourceFlow, target) => {
    if (!sourceFlow) return true; // cannot judge -> keep
    try {
      const verdict = isActionAllowed(
        action,
        sourceFlow,
        target,
        surveyLists,
        cleaned,
        undefined, // destinationLocation: irrelevant for SET_*_JUMP
        undefined // additionalProperties: undefined -> skip answerLogicJump rules
      );
      return !verdict || verdict.allowed !== false;
    } catch (error) {
      return true; // never remove on an unexpected error
    }
  };

  // ------------------------------------------- 1. question / answer / range

  // `questions` is an object keyed by question id, but we key off each
  // question's own `id` rather than the map key so this also holds if the
  // collection is ever an array (where keys would be positional indexes).
  Object.keys(questions).forEach(key => {
    const question = questions[key];
    if (!question) return;

    const questionId = question.id != null ? question.id : key;
    const sourceFlow = findQuestionFlow(questionId);

    // question-level jump
    if (question.nextFlow) {
      if (
        isDangling(question.nextFlow) ||
        !isJumpAllowed(
          QUESTION_DESIGNER_ACTION.SET_QUESTION_JUMP,
          sourceFlow,
          question.nextFlow
        )
      ) {
        delete question.nextFlow;
      }
    }

    // answer jumps on choices (multiple choice)
    if (Array.isArray(question.choices)) {
      question.choices.forEach(choice => {
        if (
          choice &&
          choice.nextFlow &&
          !isAnswerJumpAllowed(sourceFlow, choice.nextFlow)
        ) {
          // eslint-disable-next-line no-param-reassign
          delete choice.nextFlow;
        }
      });
    }

    // range jumps on branches (distributor / rating scale)
    if (Array.isArray(question.branches)) {
      question.branches.forEach(branch => {
        if (
          branch &&
          branch.nextFlow &&
          !isAnswerJumpAllowed(sourceFlow, branch.nextFlow)
        ) {
          // eslint-disable-next-line no-param-reassign
          delete branch.nextFlow;
        }
      });
    }
  });

  // -------------------------------------------------- 2. group-level jumps

  groups.forEach(group => {
    if (!group || !group.nextFlow) return;

    const sourceFlow = findGroupFlow(group.id);
    if (
      isDangling(group.nextFlow) ||
      !isJumpAllowed(
        QUESTION_DESIGNER_ACTION.SET_GROUP_JUMP,
        sourceFlow,
        group.nextFlow
      )
    ) {
      // eslint-disable-next-line no-param-reassign
      delete group.nextFlow;
    }
  });

  // `jumpsFrom` indexes are derived from the jumps above, so they are
  // intentionally not touched here — they get rebuilt when surveyLists is
  // regenerated from this cleaned structure.
  return { content: cleaned.content, questions: cleaned.questions };
}
